import { FC } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons'
import { deltaDirection } from 'src/pages/ThemeDetail/CampaignDataComparison/delta'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { cn } from 'src/utils/cn'
import styles from './campaignKeywords.module.scss'
import { KeywordTable } from '../interfaces'

interface CampaignKeywordsProps {
  period: string
  tables: KeywordTable[]
}

const COPY = {
  change: 'Change',
  // A campaign can be in the comparison and have collected nothing. Saying so
  // is the difference between an empty table and a broken one.
  empty: 'No keywords collected in this period.',
  keyword: 'Keyword',
  subtitle:
    'Compare keywords and their mention volume across selected campaigns.',
  title: 'Keywords Comparison',
}

// Unlike the campaign page — one table, a column per period — a campaign
// comparison gives each campaign its own table side by side, since the keywords
// themselves differ between campaigns and there is nothing to line up row for
// row.
export const CampaignKeywords: FC<CampaignKeywordsProps> = ({
  period,
  tables,
}) => (
  <div className={styles.card}>
    <div className={styles.header}>
      <h3 className={styles.title}>{COPY.title}</h3>
      <p className={styles.subtitle}>{COPY.subtitle}</p>
    </div>

    <div className={styles.tables}>
      {tables.map((table, index) => (
        <div className={styles.table} key={table.campaign}>
          <div className={styles.tableHeader}>
            <span
              className={cn(
                styles.swatch,
                index === 0 ? styles.swatchPrimary : styles.swatchComparison
              )}
            />
            <TruncatedTooltip
              className={styles.campaign}
              text={table.campaign}
            />
          </div>

          <div className={cn(styles.row, styles.headRow)}>
            <span className={styles.keywordCell}>{COPY.keyword}</span>
            <span className={styles.numberCell}>{period}</span>
            <span className={styles.changeCell}>{COPY.change}</span>
          </div>

          <div className={styles.body}>
            {!table.rows.length && <p className={styles.empty}>{COPY.empty}</p>}

            {table.rows.map((row) => {
              // The same rule as everywhere else in the report: the colour and the
              // arrow follow the number, not whether the move is good news.
              const direction = deltaDirection(row.change)

              return (
                <div className={styles.row} key={row.keyword}>
                  <span className={cn(styles.keywordCell, styles.keyword)}>
                    {row.keyword}
                  </span>
                  <span className={cn(styles.numberCell, styles.number)}>
                    {row.mentions}
                  </span>

                  <span className={styles.changeCell}>
                    <span
                      className={cn(styles.pill, {
                        [styles.pillGood]: direction === 'up',
                        [styles.pillBad]: direction === 'down',
                        [styles.pillFlat]: direction === 'flat',
                      })}
                    >
                      {direction !== 'flat' && (
                        <FontAwesomeIcon
                          className={styles.pillArrow}
                          icon={direction === 'up' ? faArrowUp : faArrowDown}
                        />
                      )}
                      {row.change}
                    </span>
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  </div>
)
